import { useEffect, useState } from 'react'

import type { CollectionProductSample } from '@lib/types'
import type { CustomizationSelectionType, SingleSelectionType } from '@lib/types/customization'

import styles from '../styles/CustomizationModal.module.scss'
import CustomizationProductDetails from './CustomizationProductDetails'

type CustomizationCardProps = {
	// buttonState?: string
	currentStep: number
	product: CollectionProductSample
	stepSelection: CustomizationSelectionType
	addSelectionToStep: (step: number, newSelection: SingleSelectionType) => void
	deleteSelectionToStep: (step: number, selectionToDelete: SingleSelectionType) => void
}

export default function CustomizationCard({
	// buttonState,
	currentStep,
	addSelectionToStep,
	deleteSelectionToStep,
	product,
	stepSelection
}: CustomizationCardProps) {
	const [showDetails, setShowDetails] = useState(false)
	const [variantIdx, setVariantIdx] = useState<number | undefined>(undefined)
	const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>(product.images[0]?.url || '')

	const variant = variantIdx !== undefined ? product.sampleVariants[variantIdx] : undefined
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const hasStockSomeVariant = product.sampleVariants.some((v) => v.hasStock)

	const productSelectedIndex = stepSelection.selections.findIndex(
		(s) => s.product.id === product.id
	)

	// If this product is selected?
	const isProductSelected = productSelectedIndex >= 0

	// console.log(`CustomizationCard - title: ${product.name} -- variantIdx: ${variantIdx}`)

	// Which variantIndex is selected from the current product
	const currentVariantIndexSelected = isProductSelected
		? product.sampleVariants.findIndex(
				(sv) => sv.id === stepSelection.selections[productSelectedIndex].variant?.id
			)
		: -1

	const maxItemsSelected = stepSelection.selections.length >= stepSelection.maxItems

	useEffect(() => {
		if (isProductSelected) {
			if (currentVariantIndexSelected >= 0) {
				setVariantIdx(currentVariantIndexSelected)
				setBackgroundImageUrl(
					product.sampleVariants[currentVariantIndexSelected].image?.url ||
						product.images[0]?.url ||
						''
				)
				return
			}
		}
		if (product.sampleVariants.length === 1) {
			setVariantIdx(0)
			setBackgroundImageUrl(product.sampleVariants[0].image?.url || product.images[0]?.url || '')
		} else {
			setVariantIdx(undefined)
			setBackgroundImageUrl(product.images[0]?.url || '')
		}
	}, [product, stepSelection.selections.length])

	// is current product selected state
	const isActive = isProductSelected
	const handleDetailsClick = () => {
		setShowDetails(!showDetails)
	}

	const updateVariantSelection = (variantId: string) => {
		const selection = product.sampleVariants.findIndex((v) => v.id === variantId)
		if (selection >= 0) {
			setVariantIdx(selection)
			setBackgroundImageUrl(
				product.sampleVariants[selection].image?.url || product.images[0]?.url || ''
			)
			setErrorMessage(null) // Clear error message if a variant is selected

			// If current product is selected to update the selection with new variant
			if (isProductSelected) {
				// variant should be sent like this because setVariantIdx may have not been completely executed
				addSelectionToStep(currentStep, { product, variant: product.sampleVariants[selection] })
			}
		}
	}

	// directly add the product for the first time
	const handleSelectProduct = (): boolean => {
		if (maxItemsSelected) {
			console.warn('Max items reached')
			return false
		}
		if (!variant) {
			setErrorMessage('You must pick an option')
			return false
		}

		addSelectionToStep(currentStep, { product, variant })
		return true
	}

	return (
		<li
			className={`w-[calc(50%-1rem)] sm:w-1/3 md:w-1/4 lg:w-1/6 xl:w-1/7 text-center mb-[55px] relative ${
				isActive ? styles.cardActive : ''
			}`}
		>
			<div
				className={`${styles.cardContent} bg-white shadow-bbx-card mb-[12px] h-full flex flex-col justify-between`}
			>
				<div className={`${styles.cardActiveHeader} flex flex-col z-10`}>
					<span>Your Choice</span>
					<button
						type="button"
						className="absolute right-0"
						title="Remove Item"
						onClick={() => deleteSelectionToStep(currentStep, { product, variant: variant! })}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							height="30px"
							viewBox="0 -960 960 960"
							width="30px"
							fill="#5f6368"
						>
							<path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
						</svg>
					</button>
				</div>

				<div
					className="relative w-full aspect-[1] bg-contain bg-center bg-no-repeat"
					style={{ backgroundImage: `url(${backgroundImageUrl}&height=400&width=400)` }}
				/>

				<div
					className={`py-[16px] px-[8px] flex flex-col justify-between flex-grow ${
						!hasStockSomeVariant ? 'bg-gray-100' : ''
					}`}
				>
					<div>
						<h4 className="font-bold text-[12px] md:text-[13px]">{product.vendor}</h4>
						<p className="mb-[16px] text-[14px] md:text-[14px]">{variant?.title || product.name}</p>
					</div>
					{product.sampleVariants.length > 1 && hasStockSomeVariant && (
						<div className={` ${errorMessage ? 'error' : ''} form-control mb-[16px]`}>
							<div className="w-full select cardSelect">
								<select
									value={variantIdx !== undefined ? product.sampleVariants[variantIdx]?.id : ''}
									onChange={(event) => {
										updateVariantSelection(event.target.value)
									}}
									name="variant-selection"
									id="variant-selection"
								>
									<option value="" disabled>
										-select color-
									</option>
									{product.sampleVariants.map((sampleVariant) => (
										<option
											key={sampleVariant.id}
											value={sampleVariant.id}
											disabled={!sampleVariant.hasStock}
										>
											{sampleVariant.title}
										</option>
									))}
								</select>
								{errorMessage && (
									<p className="text-bbx-red text-sm mt-[1px] absolute left-0 right-0">
										{errorMessage}
									</p>
								)}
							</div>
						</div>
					)}
					<div
						className={`${
							!isProductSelected && maxItemsSelected ? styles.selectedBtn : ''
						} relative`}
					>
						<button
							id={styles.selectButton}
							type="button"
							className={`button button-secondary py-[6px] px-[30px] ${
								!hasStockSomeVariant || maxItemsSelected ? 'disabled' : ''
							}`}
							onClick={handleSelectProduct}
						>
							{!hasStockSomeVariant ? 'Out of Stock' : 'Select'}
						</button>
						<button
							id={styles.selectedButton}
							type="button"
							className="button py-[6px] px-[30px] disabled"
						>
							Selected
						</button>
						{/* Notification message: no selection made */}
						<p className="hidden">
							<strong>
								You&#39;ve selected {stepSelection.selections.length} of {stepSelection.maxItems}{' '}
								items
							</strong>
							<br />
							Remove one to add this item
						</p>
					</div>
				</div>
			</div>
			<button
				type="button"
				className="button button-link text-bbx-style absolute m-auto left-0 right-0"
				onClick={handleDetailsClick}
			>
				Details
			</button>
			{showDetails && (
				<CustomizationProductDetails
					handleCloseDetails={() => setShowDetails(false)}
					product={product}
					variant={variant}
					onSelect={handleSelectProduct}
					updateVariantSelection={updateVariantSelection}
					errorMessage={errorMessage}
					isSelected={isProductSelected}
					isLimitSelections={maxItemsSelected}
					maxSelections={stepSelection.maxItems}
				/>
			)}
		</li>
	)
}
